/**
 * Draws the open Vest position's entry, TP and SL as price lines on a `.VEST`
 * chart (#636). Plain lightweight-charts price lines; since #639 a TP / SL
 * line follows the drag / draft / in-flight state in vestTpslStore (the
 * handles that drive it are VestTpslHandles.tsx). Entries stay off.
 *
 * Every other chart (NQ / MNQ / ES / MES ...) gets nothing: the rows are not
 * even read, no line is created, and the Tradovate position / order lines are
 * a separate path this hook never touches.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { IPriceLine } from 'lightweight-charts';
import { isVestFeedSymbol, vestTickSize } from '../../utils/vestFeed';
import {
  clearVestPriceLines,
  deriveVestPositionLines,
  syncVestPriceLines,
  type PriceLineHost,
  type VestPriceLineSpec,
} from '../../utils/vestPositionLines';
import { useVestPositionRows } from '../../store/vestPositionsStore';
import { useVestTpslStore } from '../../store/vestTpslStore';

const NO_LINES: readonly VestPriceLineSpec[] = [];

export function useVestPositionLines(
  /** The price series' ref (any series type: only its price-line methods are used). */
  seriesRef: { readonly current: unknown },
  seriesVersion: number,
  symbol: string,
  lastPrice: number | null | undefined,
): void {
  const isVest = isVestFeedSymbol(symbol);
  const rows = useVestPositionRows(isVest);
  const tick = isVest ? vestTickSize(symbol) : null;
  // Off a .VEST chart the price is not even a dependency: ticks re-run nothing.
  const price = isVest ? lastPrice ?? null : null;
  const legs = useVestTpslStore(s => s.legs);
  const overrides = isVest ? legs : null;
  const specs = useMemo(
    () => (isVest ? deriveVestPositionLines(rows, symbol, price, tick, overrides) : NO_LINES),
    [isVest, rows, symbol, price, tick, overrides],
  );

  const held = useRef(new Map<string, { line: IPriceLine; spec: VestPriceLineSpec }>());

  // A new series (symbol / timeframe rebuild) or unmount: drop every line.
  useEffect(() => {
    const series = (seriesRef.current ?? null) as PriceLineHost<IPriceLine> | null;
    const map = held.current;
    return () => clearVestPriceLines(series, map);
  }, [seriesRef, seriesVersion, symbol]);

  useEffect(() => {
    const series = (seriesRef.current ?? null) as PriceLineHost<IPriceLine> | null;
    if (!series) return;
    syncVestPriceLines(series, held.current, specs);
  }, [seriesRef, seriesVersion, symbol, specs]);
}
